import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { User, Building2, Sparkles, Target, Users, Briefcase, ShieldCheck } from "lucide-react";
import logoImage from "@/assets/logo-corre-digital.png";

export default function RoleSelection() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg space-y-8"
      >
        {/* Logo */}
        <div className="text-center">
          <motion.img
            src={logoImage}
            alt="Corre Digital"
            className="h-16 mx-auto mb-6"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          />
          <h1 className="font-heading text-2xl font-bold text-foreground">
            Bem-vindo ao CorreTalent
          </h1>
          <p className="mt-2 text-muted-foreground">
            Escolha seu acesso para continuar.
          </p>
        </div>

        {/* Como funciona - 2 cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {/* Para Alunos */}
          <div className="bg-primary/5 rounded-2xl p-4 border border-primary/10">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-5 h-5 text-primary" />
              <span className="font-semibold text-foreground text-sm">Para Alunos</span>
            </div>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <Sparkles className="w-3 h-3 mt-0.5 text-accent shrink-0" />
                Crie seu perfil progressivo
              </li>
              <li className="flex items-start gap-2">
                <Target className="w-3 h-3 mt-0.5 text-accent shrink-0" />
                Gere evidências (experiência + aquecimento)
              </li>
              <li className="flex items-start gap-2">
                <Briefcase className="w-3 h-3 mt-0.5 text-accent shrink-0" />
                Aprenda e aplique para vagas
              </li>
            </ul>
          </div>

          {/* Para Empresas */}
          <div className="bg-accent/5 rounded-2xl p-4 border border-accent/10">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-accent" />
              <span className="font-semibold text-foreground text-sm">Para Empresas</span>
            </div>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <Users className="w-3 h-3 mt-0.5 text-primary shrink-0" />
                Acesse talentos com evidências
              </li>
              <li className="flex items-start gap-2">
                <Briefcase className="w-3 h-3 mt-0.5 text-primary shrink-0" />
                Publique vagas de entrada
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="w-3 h-3 mt-0.5 text-primary shrink-0" />
                Acompanhe com governança
              </li>
            </ul>
          </div>
        </motion.div>

        {/* Role Cards */}
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              variant="role-card"
              size="xl"
              className="w-full"
              onClick={() => navigate("/aluno/landing")}
            >
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="font-heading font-semibold text-base sm:text-lg">Entrar como Aluno</p>
                  <p className="text-xs sm:text-sm text-muted-foreground font-normal">
                    Talentos e oportunidades
                  </p>
                </div>
              </div>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              variant="role-card"
              size="xl"
              className="w-full"
              onClick={() => navigate("/empresa/login")}
            >
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-accent/20 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <p className="font-heading font-semibold text-base sm:text-lg">Empresas e Parceiros</p>
                  <p className="text-xs sm:text-sm text-muted-foreground font-normal">
                    Impacto e acesso a talentos
                  </p>
                </div>
              </div>
            </Button>
          </motion.div>

          {/* Admin access through empresa login */}
        </div>

        {/* Privacy message */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-muted-foreground"
        >
          Você sempre controla seus dados e sua visibilidade.
        </motion.p>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-xs text-muted-foreground"
        >
          Ao continuar, você concorda com nossos{" "}
          <a href="#" className="text-primary hover:underline">
            Termos de Uso
          </a>{" "}
          e{" "}
          <a href="#" className="text-primary hover:underline">
            Política de Privacidade
          </a>
          .
        </motion.p>
      </motion.div>
    </div>
  );
}
